import React, {useContext, useMemo, useReducer, useState} from 'react';
import {Menu} from '@headlessui/react';
import {useNavigate} from 'react-router-dom';
import {Constants} from '../../../helpers';
import {useQuery, useQueryClient} from 'react-query';
import {useDispatch, useSelector} from 'react-redux';
import moment from 'moment/moment';
import {
  DashboardLayout,
  EmptyState,
  Loader,
  PageLimit,
  Pagination,
  PrimaryButton,
  SearchInput,
  SecondaryButton,
  TableLayout,
} from '../../../components';

import {
  copyGrey,
  edit,
  link,
  lookupEmpty,
  trashRed,
} from '../../../assets/images/images';
import Dropdown from '../../../components/TeamsDropdown';
import {fetchAuthWidgets} from '../../../requests/queries/easyAuth';
import {EasyOnboardContext} from '../../../helpers/context';
import * as Actions from '../../../store/actions';
import {toast} from 'react-toastify';
import DeleteFlowModal from '../../EasyOnboard/ConfirmFlowDelete';
import FilterV2 from 'components/FilterV2';
import PageNumber from 'components/PageNumber';
import {useAllApps} from '../../../hooks/useApps';

function AuthenticationFlow() {
  const {userDetails} = useSelector(state => state?.auth);
  const {setEasyAuthWidgetData} = useContext(EasyOnboardContext);
  const navigate = useNavigate();
  const [filterValues, setFilterValues] = useState({});
  const [showFilter, setShowFilter] = useState(false);
  const {getAppNameById} = useAllApps();

  const handleFilterChange = newValues => {
    setFilterValues(newValues);
  };

  const [{deleteModal, widgetId, searchTerm}, setState] = useReducer(
    (state, action) => ({...state, ...action}),
    {
      deleteModal: false,
      widgetId: '',
      searchTerm: '',
    },
  );

  const dispatch = useDispatch();
  const [query, setQuery] = useState({
    page: 1,
    limit: 10,
  });
  const queryClient = useQueryClient();

  const pageClick = selected => {
    setQuery({
      ...query,
      page: selected,
    });
  };

  const dateParams = filterValues.dateRange
    ? {
        start: filterValues.dateRange.start,
        end: filterValues.dateRange.end,
      }
    : {};

  const {
    data: allEasyAuthWidget,
    isLoading,
    isFetching,
  } = useQuery(
    ['all-auth-widgets', {...query, name: searchTerm, ...dateParams}],
    () => fetchAuthWidgets({...query, name: searchTerm, ...dateParams}),
  );

  const handlePreview = id => {
    queryClient.invalidateQueries('auth-widget');
    navigate(
      Constants.PAGES_URL.AUTHENTICATION_FLOW_UPDATE?.replace(':slug', id),
    );
    setEasyAuthWidgetData({name: ''});
    dispatch(Actions.setSingleWidget(null));
  };
  const copyText = text => {
    navigator.clipboard.writeText(text);
    toast.success('Link copied');
  };

  const allAuthWidget = useMemo(
    () => !isLoading && allEasyAuthWidget?.widgets,
    [allEasyAuthWidget?.widgets, isLoading],
  );

  const isAuthWidgetEmpty = useMemo(
    () => !isLoading && allAuthWidget?.length === 0,
    [allAuthWidget?.length, isLoading],
  );

  const hasWidgetUrl = !!localStorage.subdomain && !!userDetails?.widgetUrl;
  const customWidgetUrl = hasWidgetUrl
    ? userDetails?.widgetUrl
    : userDetails?.company?.widget_url;

  const {manageFlows: canManageFlows} =
    useMemo(
      () => userDetails?.permissions?.onboardPermissions,
      [userDetails],
    ) || {};

  const toggleFilterVisibility = () => {
    setShowFilter(prevShowFilter => !prevShowFilter);
  };

  return (
    <DashboardLayout
      breadCrumbs={
        <p className="text-tiny font-normal text-deepBlue">
          Authentication Flows
        </p>
      }
    >
      <DeleteFlowModal
        deleteModal={deleteModal}
        setDeleteModal={setState}
        flowId={widgetId}
      />
      <div className="flex items-center py-2 flex-wrap gap-3 justify-between mx-[-32px] px-8 border border-transparent border-b-[#E4E4E6]">
        <SearchInput
          onChange={value => {
            setQuery({...query, page: 1});
            setState({searchTerm: value});
          }}
        />
        <div className="flex items-center gap-2">
          <PrimaryButton
            onClick={() => {
              navigate(Constants.PAGES_URL.AUTHENTICATION_FLOW_CUSTOM);
              setEasyAuthWidgetData({name: ''});
              dispatch(Actions.setSingleWidget(null));
            }}
            buttonText="Create A Flow"
          />
          <SecondaryButton
            buttonText="Filter"
            onClick={toggleFilterVisibility}
          />
        </div>
      </div>

      <div className={`filter-slide-container ${showFilter ? 'open' : ''}`}>
        <FilterV2 onFilterChange={handleFilterChange} filters={[]} />
      </div>

      <div className="w-full">
        {isLoading ? (
          <Loader height={45} />
        ) : !isLoading && isAuthWidgetEmpty ? (
          <EmptyState
            height={70}
            body="Authenticate your users seamlessly"
            src={lookupEmpty}
            noBtn
            customBtn={
              <PrimaryButton
                buttonText="Create Flow"
                className="w-[200px] mt-3"
                onClick={() => {
                  navigate(Constants.PAGES_URL.AUTHENTICATION_FLOW_CUSTOM);
                  setEasyAuthWidgetData({name: ''});
                  dispatch(Actions.setSingleWidget(null));
                }}
              />
            }
          />
        ) : (
          <TableLayout negativeMargins className="pt-6">
            <thead className="font-semibold uppercase">
              <tr className="bg-white80">
                <th className="pl-4 sm:pl-6 xl:pl-8 h-11 font-medium text-left text-small text-[#5C688E] whitespace-nowrap w-[24.9%]">
                  <span className="flex items-center">name of flow</span>
                </th>
                <th className="pl-0 font-medium text-left h-11 text-small text-[#5C688E] whitespace-nowrap w-[18.8%]">
                  <span className="flex items-center">app name</span>
                </th>
                <th className="pl-0 font-medium text-left h-11 text-small text-[#5C688E] whitespace-nowrap w-[22.9%]">
                  <span className="flex items-center">status</span>
                </th>
                <th className="pl-0 font-medium text-left h-11 text-small text-[#5C688E] whitespace-nowrap w-[20%]">
                  <span className="flex items-center">date created</span>
                </th>
                <th className="w-[9.1%]"></th>
              </tr>
            </thead>
            <tbody
              className="text-sm bg-white"
              style={{opacity: isLoading || isFetching ? 0.4 : 1}}
            >
              {allAuthWidget?.map(widget => (
                <tr
                  key={widget?._id}
                  className="border-[0.5px] border-[#E4E3EC]"
                >
                  <td
                    onClick={() => handlePreview(widget?._id)}
                    className="p-4 pl-4 sm:pl-6 xl:pl-8 text-sm text-body whitespace-nowrap"
                  >
                    {widget?.name}
                  </td>
                  <td
                    onClick={() => handlePreview(widget?._id)}
                    className="p-4 pl-0 text-sm text-body whitespace-nowrap cursor-pointer w-[18.8%]"
                  >
                    {getAppNameById(widget?.app) || 'N/A'}
                  </td>
                  <td
                    onClick={() => handlePreview(widget?._id)}
                    className="p-4 pl-0 text-sm text-body whitespace-nowrap  w-fit"
                  >
                    <div
                      className={`px-2 py-1 pt-[7px] text-xs font-medium uppercase rounded-full  w-fit ${
                        widget?.published
                          ? 'text-success20 bg-success'
                          : 'text-white bg-black'
                      }`}
                    >
                      {widget?.published ? 'Published' : 'Draft'}
                    </div>
                  </td>
                  <td
                    onClick={() => handlePreview(widget?._id)}
                    className="p-4 pl-0 text-sm text-body whitespace-nowrap w-[22.9%]"
                  >
                    {moment(widget?.createdAt).format('MMM DD, YYYY hh:mm A') ||
                      '-'}
                  </td>
                  <td>
                    <div className="flex justify-center w-[9.1%]">
                      <Dropdown>
                        <Menu.Item>
                          {() => (
                            <button
                              onClick={() => handlePreview(widget?._id)}
                              className="z-40 flex items-center gap-4 w-full font-medium text-gray-500 text-tiny"
                            >
                              <img src={edit} alt="" width={14} height={14} />
                              {!canManageFlows ? 'View' : 'Edit'}
                            </button>
                          )}
                        </Menu.Item>
                        {widget?.published && (
                          <Menu.Item>
                            {() => (
                              <button
                                onClick={() =>
                                  window.open(
                                    `${
                                      customWidgetUrl ??
                                      process.env.REACT_APP_WIDGET_BASE_URL
                                    }/register?widget_id=${widget?._id}`,
                                    '_blank',
                                  )
                                }
                                className="z-40 flex items-center gap-4 w-full font-medium text-gray-500 text-tiny"
                              >
                                <img src={link} alt="" width={14} height={14} />
                                Visit
                              </button>
                            )}
                          </Menu.Item>
                        )}
                        <Menu.Item>
                          {() => (
                            <button
                              onClick={() =>
                                copyText(
                                  `${
                                    customWidgetUrl ??
                                    process.env.REACT_APP_WIDGET_BASE_URL
                                  }/authenticate?widget_id=${widget?._id}`,
                                )
                              }
                              className="z-40 flex items-center gap-4 w-full font-medium text-gray-500 text-tiny"
                            >
                              <img
                                src={copyGrey}
                                alt=""
                                width={14}
                                height={14}
                              />
                              Copy
                            </button>
                          )}
                        </Menu.Item>
                        <Menu.Item>
                          {() => (
                            <button
                              onClick={() => {
                                setState({
                                  deleteModal: true,
                                  widgetId: widget?._id,
                                });
                              }}
                              className="z-40 flex  items-center gap-4 w-full font-medium text-danger text-tiny"
                            >
                              <img
                                src={trashRed}
                                alt=""
                                width={14}
                                height={14}
                              />
                              Delete
                            </button>
                          )}
                        </Menu.Item>
                      </Dropdown>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </TableLayout>
        )}

        {!isLoading && allAuthWidget && !isAuthWidgetEmpty && (
          <div className="flex flex-wrap items-center justify-between gap-4 sm:gap-0 bg-white80 mx-[-32px] px-8 py-[18px] border-[0.5px] border-y-[#E4E3EC] border-transparent">
            <PageNumber
              currentPage={+query?.page}
              limit={query?.limit}
              totalCount={allEasyAuthWidget?.totalPages * query?.limit}
            />

            <div className="flex items-center gap-5">
              <PageLimit
                onLimitChange={limit => {
                  setQuery(prevState => ({
                    ...prevState,
                    limit,
                    page: 1,
                  }));
                }}
                limit={query?.limit}
                total={allEasyAuthWidget?.totalPages}
                length={allAuthWidget?.length}
              />

              <Pagination
                total={allEasyAuthWidget?.totalPages}
                current={Number(query?.page)}
                onPageChange={activePage => {
                  pageClick(activePage);
                  const searchParams = new URLSearchParams(
                    document.location.search,
                  );
                  searchParams.set('page', activePage);
                }}
              />
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

export default AuthenticationFlow;
